import { createServerFn } from "@tanstack/react-start"
import { minBy } from "lodash-es"
import { z } from "zod"

import { DigestLabelVerdict } from "@/constants/terminal/digest-label-verdict"
import type { DraftFacts } from "@/terminal/digest/describe-file-change"
import type { FileChange } from "@/terminal/digest/file-change"

// The server-only modules load inside each handler, so the page's bundle
// never reaches the database client.

// The Policy Tuner's Compilations, which the page replays a few at a time,
// each Portfolio's monitored Companies, the monitored ones with no scored
// Compilation to replay, and every label so far.
export const loadDigestTuner = createServerFn({ method: "GET" }).handler(
  async () => {
    const [
      { assertAdminActor },
      { listTunerCompilations },
      { monitoredCompaniesByPortfolio },
      { listDigestLabels },
      { db },
      { inArray },
      { terminalCompany },
    ] = await Promise.all([
      import("@/lib/supabase/auth"),
      import("@/admin/policy-tuner/tuner-cases"),
      import("@/terminal/digest/file-history"),
      import("@/admin/digest-tuner/digest-labels"),
      import("@/lib/admin-db"),
      import("drizzle-orm"),
      import("@/db/schema/terminal-company-identity"),
    ])
    // A server function is directly callable, so the route guard is not enough.
    const viewer = await assertAdminActor()
    const [list, portfolios, labels] = await Promise.all([
      listTunerCompilations(),
      monitoredCompaniesByPortfolio(db, new Date()),
      listDigestLabels(),
    ])
    const monitoredIds = new Set(
      portfolios.flatMap(({ companyIds }) => companyIds)
    )
    const scoredIds = new Set(
      list.compilations.map(({ companyId }) => companyId)
    )
    const unscoredIds = [...monitoredIds].filter((id) => !scoredIds.has(id))
    const unscoredCompanies =
      unscoredIds.length === 0
        ? []
        : await db
            .select({
              companyId: terminalCompany.id,
              name: terminalCompany.display_name,
            })
            .from(terminalCompany)
            .where(inArray(terminalCompany.id, unscoredIds))
    return {
      ...list,
      // Only monitored Companies replay, but the weeks end where the thinnest
      // evidence of any Company's latest Compilation does.
      compilations: list.compilations.filter(({ companyId }) =>
        monitoredIds.has(companyId)
      ),
      evidenceThrough: minBy(list.compilations, ({ cutoff }) =>
        Date.parse(cutoff)
      )?.cutoff,
      portfolios,
      unscoredCompanies,
      labels,
      viewer,
      generatedAt: new Date().toISOString(),
    }
  }
)

export type DigestTunerData = Awaited<ReturnType<typeof loadDigestTuner>>

// One Compilation as a case, without the Policy Tuner's parity check, which
// the simulation never reads.
export const loadDigestTunerCase = createServerFn({ method: "GET" })
  .inputValidator(z.object({ compilationId: z.string().uuid() }))
  .handler(async ({ data }) => {
    const [{ assertAdminContext }, { buildTunerCase }] = await Promise.all([
      import("@/lib/supabase/auth"),
      import("@/admin/policy-tuner/tuner-cases"),
    ])
    await assertAdminContext()
    const { tunerCase } = await buildTunerCase(data.compilationId)
    return { tunerCase }
  })

export const loadChangeEvidence = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      compilationId: z.string().uuid(),
      previousCheck: z.string().datetime(),
      check: z.string().datetime(),
      scoreInputKeys: z.array(z.string().min(1)).max(40),
    })
  )
  .handler(async ({ data }) => {
    const [{ assertAdminContext }, evidence] = await Promise.all([
      import("@/lib/supabase/auth"),
      import("@/admin/digest-tuner/change-evidence"),
    ])
    await assertAdminContext()
    return evidence.loadChangeEvidence(data)
  })

// The draft reason in the Detection Board's voice, over the diff and the
// facts the page adds: the evidence the admin chose, the customer base and
// the recent Files. One Model Call, ledgered as the weekly Digest's are.
export const writeChangeReason = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      change: z.custom<FileChange>(
        (value) =>
          typeof value === "object" && value !== null && "companyName" in value
      ),
      facts: z.custom<DraftFacts>(
        (value) =>
          typeof value === "object" && value !== null && "asOf" in value
      ),
    })
  )
  .handler(async ({ data: { change, facts } }) => {
    const [{ assertAdminContext }, { writeDraftReason }] = await Promise.all([
      import("@/lib/supabase/auth"),
      import("@/terminal/digest/describe-file-change"),
    ])
    await assertAdminContext()
    return writeDraftReason(change, facts)
  })

export const saveChangeLabel = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      companyId: z.string().uuid(),
      previousCheck: z.string().datetime(),
      check: z.string().datetime(),
      verdict: z.nativeEnum(DigestLabelVerdict).nullable(),
    })
  )
  .handler(async ({ data }) => {
    const [{ assertAdminActor }, { saveDigestLabel }] = await Promise.all([
      import("@/lib/supabase/auth"),
      import("@/admin/digest-tuner/digest-labels"),
    ])
    return saveDigestLabel(await assertAdminActor(), data)
  })
